import React from 'react';
import { Message as MessageType, ResearchPaper } from '../types';
import { WebSearchResult } from '../services/api';
import MarkdownRenderer from './MarkdownRenderer';
import CitationsDisplay from './CitationsDisplay';
import ResearchPapersDisplay from './ResearchPapersDisplay';
import FeedbackButtons, { FeedbackData } from './FeedbackButtons';
import feedbackService from '../services/feedback.service';
import { detectMentionedProducts } from '../utils/pawcoProducts';

interface MessageProps {
  message: MessageType;
  isStreaming?: boolean;
  webSearchResults?: WebSearchResult[];
  researchPapers?: ResearchPaper[];
  userQuestion?: string;
  appMode?: 'pawco' | 'genius-dog';
}

const Message: React.FC<MessageProps> = ({ 
  message, 
  isStreaming = false, 
  webSearchResults, 
  researchPapers,
  userQuestion = '',
  appMode = 'pawco'
}) => {
  const handleFeedbackSubmit = async (feedbackData: FeedbackData) => {
    try {
      await feedbackService.submitFeedback(feedbackData);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      throw error;
    }
  };

  const formatMessage = (text: string): JSX.Element => {
    // For user messages, keep simple formatting
    const formatted = text
      .replace(/\n/g, '<br>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>');

    return <span dangerouslySetInnerHTML={{ __html: formatted }} />;
  };

  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className={`message-wrapper ${message.sender}-message ${isStreaming ? 'streaming' : ''}`}>
      <div className="message-avatar">
        {message.sender === 'bot' ? (
          <img src="/NewPawcoLogoMinimal.png" alt="Pawco" className="avatar-logo" />
        ) : (
          <i className="fas fa-user"></i>
        )}
      </div>
      <div className="message-content">
        <div className="message-bubble">
          <div className="message-header">
            <span className="sender-name">
              {message.sender === 'bot' ? 'Pawco Assistant' : 'You'}
            </span>
            <span className="message-time">{formatTime(message.timestamp)}</span>
          </div>
          <div className="message-text">
            {message.sender === 'bot' ? (
              <>
                <MarkdownRenderer
                  content={message.text}
                  className={isStreaming ? 'streaming' : ''}
                />
                {!isStreaming && (() => {
                  const products = detectMentionedProducts(message.text);
                  const hasContactLink =
                    message.text.includes('mypawco.com/pages/contact-us') ||
                    message.text.includes('help@mypawco.com');

                  return (
                    <>
                      {products.length > 0 && (
                        <div className="pawco-shop-strip">
                          <span className="pawco-shop-strip__label">Shop mentioned products</span>
                          <div className="pawco-shop-strip__buttons">
                            {products.map(p => (
                              <a
                                key={p.url}
                                href={p.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="pawco-shop-btn"
                              >
                                <svg className="pawco-shop-btn__icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                  <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                                </svg>
                                {p.name}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                      {hasContactLink && (
                        <div className="pawco-contact-card">
                          <div className="pawco-contact-card__icon">
                            <i className="fas fa-headset"></i>
                          </div>
                          <div className="pawco-contact-card__body">
                            <span className="pawco-contact-card__title">Need more help?</span>
                            <span className="pawco-contact-card__subtitle">Our team is happy to assist you.</span>
                          </div>
                          <div className="pawco-contact-card__actions">
                            <a
                              href="mailto:help@mypawco.com"
                              className="pawco-contact-btn pawco-contact-btn--email"
                            >
                              <i className="fas fa-envelope"></i>
                              help@mypawco.com
                            </a>
                            <a
                              href="https://www.mypawco.com/pages/contact-us"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="pawco-contact-btn pawco-contact-btn--page"
                            >
                              <i className="fas fa-arrow-right"></i>
                              Contact Us
                            </a>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
                {webSearchResults && webSearchResults.length > 0 && (
                  <div className="integrated-citations">
                    <CitationsDisplay results={webSearchResults} />
                  </div>
                )}
                {researchPapers && researchPapers.length > 0 && (
                  <div className="integrated-citations">
                    <ResearchPapersDisplay papers={researchPapers} />
                  </div>
                )}
              </>
            ) : (
              <>
                {formatMessage(message.text)}
                {isStreaming && <span className="streaming-cursor"></span>}
              </>
            )}
          </div>
          {message.sender === 'bot' && !isStreaming && (
            <FeedbackButtons
              messageId={message.id}
              userQuestion={userQuestion}
              botResponse={message.text}
              appMode={appMode}
              onFeedbackSubmit={handleFeedbackSubmit}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Message;
